
import React, { useState, useEffect, useId, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { postService, Post, Comment, PostListResponse } from '../services/postService';
import { postCategoryService } from '../services/postCategoryService';
import { formatContent } from '../utils/formatContent';
import { isSchoolTrackMatch } from '../utils/schoolTracks';
import { getMediaUrl } from '../utils/media';
import { socketService } from '../services/socketService';
import { taskService } from '../services/taskService';
import { Level, UserLevel, PostCategory } from '../types';
import NewYearEventBanner from '../components/NewYearEventBanner';
import './CommunityPage.css';

const CommunityPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false); // Start false - UI shows immediately
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: [] as string[],
    category_id: null as number | null,
  });
  const [tagInput, setTagInput] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [postCategories, setPostCategories] = useState<PostCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [activeCategorySlug, setActiveCategorySlug] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentContent, setCommentContent] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [submittingReplyTo, setSubmittingReplyTo] = useState<number | null>(null);
  const [likingPostId, setLikingPostId] = useState<number | null>(null);
  const [likingCommentId, setLikingCommentId] = useState<number | null>(null);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [lastDoubleClickTime, setLastDoubleClickTime] = useState<number>(0);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [commentVideoFile, setCommentVideoFile] = useState<File | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [replyAttachedFile, setReplyAttachedFile] = useState<File | null>(null);
  const [replyLinkUrl, setReplyLinkUrl] = useState('');
  const [replyVideoUrl, setReplyVideoUrl] = useState('');
  const [replyVideoFile, setReplyVideoFile] = useState<File | null>(null);
  const [replyShowLinkModal, setReplyShowLinkModal] = useState(false);
  const [replyShowVideoModal, setReplyShowVideoModal] = useState(false);
  const [replyShowEmojiPicker, setReplyShowEmojiPicker] = useState(false);
  const [replyShowGifPicker, setReplyShowGifPicker] = useState(false);
  const [attachedPreviewUrl, setAttachedPreviewUrl] = useState<string | null>(null);
  const [replyAttachedPreviewUrl, setReplyAttachedPreviewUrl] = useState<string | null>(null);
  const [commentVideoPreviewUrl, setCommentVideoPreviewUrl] = useState<string | null>(null);
  const [replyVideoPreviewUrl, setReplyVideoPreviewUrl] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [expandedComments, setExpandedComments] = useState<Record<number, boolean>>({});
  const [showCommentActions, setShowCommentActions] = useState(false);
  const [showReplyActions, setShowReplyActions] = useState(false);
  const [openPostMenuId, setOpenPostMenuId] = useState<number | null>(null);
  const [editingPostId, setEditingPostId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState({ title: '', content: '', tags: [] as string[], category_id: null as number | null });
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportPostId, setReportPostId] = useState<number | null>(null);
  const [reportReason, setReportReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectPostId, setRejectPostId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [postNotifications, setPostNotifications] = useState<Record<number, boolean>>(() => {
    // Load from localStorage on mount
    try {
      const stored = localStorage.getItem('postNotifications');
      return stored ? JSON.parse(stored) : {};
    } catch (err) {
      console.error('Error loading notification preferences:', err);
      return {};
    }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);
  const [userLevel, setUserLevel] = useState<UserLevel | null>(null);
  const [levels, setLevels] = useState<Level[]>([]);
  const [showNewYearBanner, setShowNewYearBanner] = useState(false);
  const emojiOptions = [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
    '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
    '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕',
    '🤢', '🤮', '🤧', '🥵', '🥶', '😶‍🌫️', '😵', '😵‍💫', '🤯', '🤠', '🥳', '😎', '🤓', '🧐', '😕',
    '😟', '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱',
    '😖', '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩',
    '🤡', '👹', '👺', '👻', '👽', '👾', '🤖', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'
  ];
  const commentFileInputId = useId();
  const replyFileInputId = useId();
  const commentVideoFileInputId = useId();
  const replyVideoModalFileInputId = useId();
  const replyTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const commentActionsRef = useRef<HTMLDivElement | null>(null);
  const replyActionsRef = useRef<HTMLDivElement | null>(null);
  // apiBaseUrl removed in favor of getMediaUrl
  const fetchPostsAbortController = useRef<AbortController | null>(null);
  const isFetchingPosts = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const searchQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search')?.trim() || '';
  }, [location.search]);

  const clearSearchQuery = useCallback(() => {
    if (!searchQuery) {
      return;
    }
    const params = new URLSearchParams(location.search);
    params.delete('search');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, searchQuery]);

  const categoryFilters = useMemo(() => {
    const baseFilters = [
      {
        slug: null as string | null,
        label: t('filters.all'),
        icon: '✨',
      },
    ];
    const dynamicFilters = [...postCategories].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)).map((cat) => ({
      slug: cat.slug,
      label: cat.name,
      icon: cat.icon || '',
      color: cat.color,
      id: cat.id,
    }));
    return [...baseFilters, ...dynamicFilters];
  }, [postCategories, t]);

  const quickActions = useMemo(() => {
    const dynamic = categoryFilters
      .filter((filter) => filter.slug)
      .slice(0, 4)
      .map((filter) => ({
        label: filter.label,
        icon: filter.icon || '🏷️',
        slug: filter.slug,
      }));
    const fallbackQuickActions = [
      { label: t('community.quickActions.video'), icon: '📹', slug: undefined },
      { label: t('community.quickActions.tips'), icon: '🧠', slug: undefined },
      { label: t('community.quickActions.aiPrompt'), icon: '🤖', slug: undefined },
      { label: t('community.quickActions.questions'), icon: '❓', slug: undefined },
    ];
    return dynamic.length > 0 ? dynamic : fallbackQuickActions;
  }, [categoryFilters, t]);


  // Kiểm tra xem có cần hiển thị banner chúc mừng năm mới không
  useEffect(() => {
    // Chỉ check khi user đã login
    if (authLoading || !user) {
      return;
    }

    // Luôn hiển thị banner giới thiệu sự kiện, trừ khi người dùng đã đóng nó trong phiên này
    const isBannerClosed = sessionStorage.getItem('eventBannerClosed') === 'true';
    if (!isBannerClosed) {
      setShowNewYearBanner(true);
    }
  }, [user, authLoading]);

  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const data = await postCategoryService.list({ with_counts: true });
        if (isMounted) {
          setPostCategories(data);
        }
      } catch (err) {
        console.error('Error fetching post categories:', err);
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (replyTextareaRef.current) {
      const textarea = replyTextareaRef.current;
      const cursorPos = textarea.value.length;
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    }
  }, [replyingTo]);

  useEffect(() => {
    if (attachedFile && attachedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(attachedFile);
      setAttachedPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setAttachedPreviewUrl(null);
  }, [attachedFile]);

  useEffect(() => {
    if (replyAttachedFile && replyAttachedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(replyAttachedFile);
      setReplyAttachedPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setReplyAttachedPreviewUrl(null);
  }, [replyAttachedFile]);

  useEffect(() => {
    if (commentVideoFile) {
      const url = URL.createObjectURL(commentVideoFile);
      setCommentVideoPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setCommentVideoPreviewUrl(null);
  }, [commentVideoFile]);

  useEffect(() => {
    if (replyVideoFile) {
      const url = URL.createObjectURL(replyVideoFile);
      setReplyVideoPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    setReplyVideoPreviewUrl(null);
  }, [replyVideoFile]);

  useEffect(() => {
    if (!showCommentActions) return;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (commentActionsRef.current && !commentActionsRef.current.contains(event.target as Node)) {
        setShowCommentActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showCommentActions]);

  useEffect(() => {
    if (!showReplyActions) return;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (replyActionsRef.current && !replyActionsRef.current.contains(event.target as Node)) {
        setShowReplyActions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showReplyActions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.post-menu-container')) {
        setOpenPostMenuId(null);
      }
    };
    if (openPostMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openPostMenuId]);

  const isImageFile = (file: File | null) => Boolean(file && file.type.startsWith('image/'));

  const handleCategoryFilterChange = (slug: string | null) => {
    if (activeCategorySlug === slug) {
      return;
    }
    setActiveCategorySlug(slug);
    setCurrentPage(1);
    setHasMorePosts(true);
  };

  const getCategorySummary = useCallback(
    (categoryId: number | null | undefined) => {
      if (!categoryId) return null;
      const found = postCategories.find((cat) => cat.id === categoryId);
      if (!found) return null;
      return {
        id: found.id,
        name: found.name,
        slug: found.slug,
        icon: found.icon || undefined,
        color: found.color || undefined,
      };
    },
    [postCategories]
  );


  const fetchPosts = useCallback(async (pageToLoad = 1, { append = false }: { append?: boolean } = {}) => {
    // Prevent duplicate requests
    if (isFetchingPosts.current) {
      return;
    }

    try {
      // Don't cancel if we're already fetching - let the current request complete
      // Only cancel if we're starting a new fetch and there's a pending one
      if (fetchPostsAbortController.current && !isFetchingPosts.current) {
        fetchPostsAbortController.current.abort();
      }

      // Create new abort controller
      const abortController = new AbortController();
      fetchPostsAbortController.current = abortController;
      isFetchingPosts.current = true;

      // Don't set loading state - UI shows immediately
      // Only set loadingMorePosts for "load more" button
      if (append) {
        setLoadingMorePosts(true);
      }
      setError('');
      const filterParams: Record<string, any> = { page: pageToLoad, per_page: 20 };
      if (activeCategorySlug) {
        filterParams.category_slug = activeCategorySlug;
      }
      if (searchQuery) {
        filterParams.search = searchQuery;
      }
      const shouldFetchMyPosts = Boolean(
        user &&
        pageToLoad === 1 &&
        !append &&
        !activeCategorySlug &&
        !searchQuery
      );
      const postsPromise = postService.getPosts(filterParams);
      const myPostsPromise: Promise<PostListResponse | null> = shouldFetchMyPosts
        ? postService.getMyPosts({ per_page: 50 })
        : Promise.resolve(null);
      const [data, myPostsData] = await Promise.all([postsPromise, myPostsPromise]);

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      let incomingPosts = data.posts || [];
      if (myPostsData && user) {
        const myPosts = myPostsData.posts || [];
        const currentIds = new Set(incomingPosts.map(post => post.id));
        const uniqueMyPosts = myPosts.filter(post => !currentIds.has(post.id));
        incomingPosts = [...uniqueMyPosts, ...incomingPosts];
      }
      setHasMorePosts(pageToLoad < (data.pages || 1));
      setCurrentPage(pageToLoad);

      if (append) {
        setPosts(prevPosts => {
          const existingIds = new Set(prevPosts.map(post => post.id));
          const newPosts = incomingPosts.filter(post => !existingIds.has(post.id));
          return [...prevPosts, ...newPosts];
        });
      } else {
        setPosts(incomingPosts);
      }
    } catch (err: any) {
      // Ignore abort errors
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        return;
      }
      console.error('Error fetching posts:', err);
      console.error('Error details:', err.response?.data, err.message);
      const errorMessage = err.response?.data?.error || t('community.errors.loadPosts');
      setError(errorMessage);
      // Don't clear posts on error - keep existing posts
      // setPosts([]);
    } finally {
      isFetchingPosts.current = false;
      // Don't set loading - UI already shown, data loads in background
      setLoadingMorePosts(false);
    }
  }, [user, activeCategorySlug, searchQuery]);

  useEffect(() => {
    // Don't block UI - fetch posts even if auth is still loading
    // Only skip if already fetching
    if (isFetchingPosts.current) {
      return;
    }

    // If auth is still loading, wait a bit but don't block UI rendering
    // The skeleton will show immediately
    if (authLoading) {
      // Set a small timeout to fetch after auth loads, but UI already rendered
      const timeoutId = setTimeout(() => {
        if (!authLoading && !isFetchingPosts.current) {
          fetchPosts();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }

    fetchPosts();
    return () => {
      // Only cancel on unmount, not on re-fetch
      // This prevents aborting requests that are still processing
    };
  }, [authLoading, user?.id, activeCategorySlug, fetchPosts]);

  // Fetch user level and levels list
  useEffect(() => {
    const fetchLevelData = async () => {
      if (!user) return;
      try {
        const [myLevelData, levelsData] = await Promise.all([
          taskService.getMyLevel(),
          taskService.listLevels(),
        ]);
        setUserLevel(myLevelData);
        setLevels(levelsData.levels);
      } catch (err) {
        console.error('Error fetching level data:', err);
        // Set default level if fetch fails
        if (user.stats?.level) {
          setUserLevel({
            id: 0,
            user_id: user.id,
            level: user.stats.level,
            total_points: user.stats.points || 0,
            updated_at: new Date().toISOString(),
            points_to_next: user.stats.points_to_next,
          });
        }
      }
    };
    fetchLevelData();
  }, [user]);

  // Handle URL hash for post and comment navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (!hash || posts.length === 0) return;

      // Parse hash: #post-123&comment-456 or #post-123
      const postMatch = hash.match(/post-(\d+)/);
      const commentMatch = hash.match(/comment-(\d+)/);

      if (postMatch) {
        const postId = parseInt(postMatch[1]);
        const commentId = commentMatch ? parseInt(commentMatch[1]) : null;

        // Find post in current posts list
        const post = posts.find(p => p.id === postId);
        if (post) {
          handleViewDetail(postId).then(() => {
            // If comment ID exists, scroll to it after modal opens and comments load
            if (commentId) {
              // Wait for comments to load, then scroll
              const scrollToComment = () => {
                const commentElement = document.getElementById(`comment - ${commentId} `);
                if (commentElement) {
                  // Scroll within the modal's scrollable area
                  // Find the scrollable container (usually the modal body)
                  const modalBody = commentElement.closest('[class*="overflow-y-auto"]') ||
                    document.querySelector('[class*="max-h"][class*="overflow"]');
                  if (modalBody && modalBody instanceof HTMLElement) {
                    const elementTop = commentElement.offsetTop - modalBody.offsetTop;
                    modalBody.scrollTo({
                      top: elementTop - 100,
                      behavior: 'smooth'
                    });
                  } else {
                    commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                  // Highlight comment
                  commentElement.classList.add('ring-2', 'ring-blue-500', 'rounded-lg', 'transition-all', 'p-2');
                  setTimeout(() => {
                    commentElement.classList.remove('ring-2', 'ring-blue-500', 'rounded-lg', 'p-2');
                  }, 3000);
                  return true;
                }
                return false;
              };

              // Try multiple times with increasing delays
              let attempts = 0;
              const maxAttempts = 15;
              const tryScroll = () => {
                if (scrollToComment() || attempts >= maxAttempts) {
                  return;
                }
                attempts++;
                setTimeout(tryScroll, 300);
              };
              setTimeout(tryScroll, 500);
            }
          });
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Also check on mount/update
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [posts, t]);

  // Real-time updates via WebSocket
  useEffect(() => {
    if (!user) return;

    // Connect to socket if not connected
    if (!socketService.isConnected()) {
      socketService.connect();
    }

    // Listen for new posts
    const unsubscribeNewPost = socketService.on('new_post', (post: Post) => {
      setPosts(prev => {
        // Check if post already exists
        const exists = prev.some(p => p.id === post.id);
        if (exists) {
          // Update existing post
          return prev.map(p => p.id === post.id ? post : p);
        }
        // Add new post at the beginning
        return [post, ...prev];
      });
    });

    // Listen for post updates
    const unsubscribePostUpdate = socketService.on('post_update', (post: Post) => {
      setPosts(prev => prev.map(p => p.id === post.id ? post : p));
      if (selectedPost?.id === post.id) {
        setSelectedPost(post);
      }
    });

    // Listen for new comments
    const unsubscribeNewComment = socketService.on('new_comment', (data: { comment: Comment; post_id: number }) => {
      const { comment, post_id } = data;

      // Update comments if viewing this post
      if (selectedPost?.id === post_id) {
        setComments(prev => {
          const exists = prev.some(c => c.id === comment.id);
          if (exists) return prev;
          return [comment, ...prev];
        });
      }

      // Update comments count in posts list
      setPosts(prev => prev.map(p =>
        p.id === post_id
          ? { ...p, comments_count: (p.comments_count || 0) + 1 }
          : p
      ));

      // Update comments count in selected post
      if (selectedPost?.id === post_id) {
        setSelectedPost(prev => prev ? {
          ...prev,
          comments_count: (prev.comments_count || 0) + 1
        } : null);
      }
    });

    return () => {
      unsubscribeNewPost();
      unsubscribePostUpdate();
      unsubscribeNewComment();
    };
  }, [user, selectedPost]);

  const handleLoadMorePosts = useCallback(async () => {
    if (!hasMorePosts || loadingMorePosts) return;
    const nextPage = currentPage + 1;
    await fetchPosts(nextPage, { append: true });
  }, [hasMorePosts, loadingMorePosts, currentPage, fetchPosts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleCategorySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, category_id: value ? Number(value) : null }));
    setError('');
  };

  const handleEditCategorySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, category_id: value ? Number(value) : null }));
    setError('');
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Video is optional - if no file selected, clear video
    if (!file) {
      setVideoFile(null);
      setVideoPreview(null);
      return;
    }

    // Validate file size
    if (file.size > 500 * 1024 * 1024) { // 500MB
      setError(t('community.errors.videoSize'));
      setVideoFile(null);
      setVideoPreview(null);
      // Clear the input
      e.target.value = '';
      return;
    }

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('community.errors.videoFormat'));
      setVideoFile(null);
      setVideoPreview(null);
      // Clear the input
      e.target.value = '';
      return;
    }

    setVideoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setVideoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    setError('');
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setError(t('community.errors.thumbnailSize'));
        return;
      }
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleViewDetail = async (postId: number): Promise<void> => {
    try {
      const post = await postService.getPost(postId);
      setSelectedPost(post);
      setShowDetailModal(true);
      setCommentContent('');
      await fetchComments(postId);
    } catch (err: any) {
      setError(t('community.errors.loadDetail'));
    }
  };

  const fetchComments = async (postId: number) => {
    try {
      setLoadingComments(true);
      const commentsData = await postService.getComments(postId);
      setComments(commentsData);
    } catch (err: any) {
      console.error('Error fetching comments:', err);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async (postId: number) => {
    setLikingPostId(postId);
    try {
      const result = await postService.toggleLikePost(postId);
      // Update post in list
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === postId
            ? { ...post, is_liked: result.is_liked, likes_count: result.likes_count }
            : post
        )
      );
      // Update selected post if it's the same
      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost({ ...selectedPost, is_liked: result.is_liked, likes_count: result.likes_count });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.likePost'));
    } finally {
      setTimeout(() => setLikingPostId(null), 300);
    }
  };

  const handleToggleCommentLike = async (commentId: number) => {
    if (!selectedPost) return;
    setLikingCommentId(commentId);
    try {
      const result = await postService.toggleCommentLike(selectedPost.id, commentId);
      setComments(prev => updateCommentLikesState(prev, commentId, {
        likes_count: result.likes_count,
        is_liked: result.is_liked,
      }));
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.likeComment'));
    } finally {
      setTimeout(() => setLikingCommentId(null), 200);
    }
  };

  const handleDoubleClickLike = (postId: number) => {
    const now = Date.now();
    if (now - lastDoubleClickTime < 300) {
      // Double click detected
      if (!posts.find(p => p.id === postId)?.is_liked) {
        handleLike(postId);
      }
    }
    setLastDoubleClickTime(now);
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPost || (!commentContent.trim() && !attachedFile && !linkUrl && !videoUrl && !commentVideoFile)) return;

    try {
      setSubmittingComment(true);
      setSubmittingReplyTo(null);
      const result = await postService.createComment(
        selectedPost.id,
        commentContent,
        undefined,
        {
          attachment: attachedFile || undefined,
          linkUrl: linkUrl || undefined,
          videoUrl: videoUrl || undefined,
          videoFile: commentVideoFile || undefined
        }
      );
      // Clear form
      setCommentContent('');
      setAttachedFile(null);
      setLinkUrl('');
      setVideoUrl('');
      setCommentVideoFile(null);
      setShowLinkModal(false);
      setShowVideoModal(false);
      setShowEmojiPicker(false);
      setShowGifPicker(false);
      setShowCommentActions(false);

      // Refresh comments to get the latest data including the new comment
      await fetchComments(selectedPost.id);

      // Update comments count
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPost.id
            ? { ...post, comments_count: (post.comments_count || 0) + 1 }
            : post
        )
      );
      if (selectedPost) {
        setSelectedPost({ ...selectedPost, comments_count: (selectedPost.comments_count || 0) + 1 });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.addComment'));
    } finally {
      setSubmittingReplyTo(null);
      setSubmittingComment(false);
    }
  };

  const handleSubmitReply = async (e: React.FormEvent, parentId: number) => {
    e.preventDefault();
    const hasExtras = replyAttachedFile || replyLinkUrl || replyVideoUrl || replyVideoFile;
    if (!selectedPost || (!replyContent.trim() && !hasExtras)) return;

    try {
      setSubmittingReplyTo(parentId);
      setSubmittingComment(true);
      await postService.createComment(
        selectedPost.id,
        replyContent,
        parentId,
        {
          attachment: replyAttachedFile || undefined,
          linkUrl: replyLinkUrl || undefined,
          videoUrl: replyVideoUrl || undefined,
          videoFile: replyVideoFile || undefined
        }
      );

      await fetchComments(selectedPost.id);

      resetReplyComposer();

      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPost.id
            ? { ...post, comments_count: (post.comments_count || 0) + 1 }
            : post
        )
      );
      if (selectedPost) {
        setSelectedPost({ ...selectedPost, comments_count: (selectedPost.comments_count || 0) + 1 });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.replyComment'));
    } finally {
      setSubmittingComment(false);
      setSubmittingReplyTo(null);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!selectedPost) return;
    if (!window.confirm('Bạn có chắc chắn muốn xóa bình luận này?')) return;

    try {
      await postService.deleteComment(selectedPost.id, commentId);
      setComments(prev => removeCommentById(prev, commentId));

      // Update comments count
      setPosts(prevPosts =>
        prevPosts.map(post =>
          post.id === selectedPost.id
            ? { ...post, comments_count: Math.max(0, (post.comments_count || 0) - 1) }
            : post
        )
      );
      if (selectedPost) {
        setSelectedPost({ ...selectedPost, comments_count: Math.max(0, (selectedPost.comments_count || 0) - 1) });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.deleteComment'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      if (!formData.title.trim()) {
        setError(t('community.errors.missingTitle'));
        setSubmitting(false);
        return;
      }
      if (!formData.content.trim()) {
        setError(t('community.errors.missingContent'));
        setSubmitting(false);
        return;
      }



      const response = await postService.createPost({
        title: formData.title,
        content: formData.content,
        tags: formData.tags,
        video: videoFile || undefined,
        thumbnail: thumbnailFile || undefined,
        category_id: formData.category_id ?? undefined,
      });

      // Use message from backend response
      const successMessage = response.message || t('community.success.postCreated');
      setSuccess(successMessage);

      // If post requires approval, show additional info
      if (response.requires_approval || response.post?.status === 'pending') {
        setSuccess(`${successMessage}\n\n${t('community.success.postPending')}`);
      }

      setFormData({ title: '', content: '', tags: [], category_id: null });
      setVideoFile(null);
      setVideoPreview(null);
      setThumbnailFile(null);
      setThumbnailPreview(null);
      setShowCreateForm(false);

      // Don't fetch posts immediately if post is pending - it won't show in community feed anyway
      // Only fetch if post was approved (admin/instructor)
      if (!response.requires_approval && response.post?.status === 'approved') {
        if (!isFetchingPosts.current) {
          fetchPosts();
        }
      }

      setTimeout(() => setSuccess(''), 5000); // Show longer for important messages
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('community.errors.createPost');
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (postId: number) => {
    try {
      await postService.approvePost(postId);
      setSuccess(t('community.success.postApproved'));
      // Update local state instead of refetching
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, status: 'approved' as const } : p));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.generic'));
    }
  };

  const handleReject = async (postId: number) => {
    setRejectPostId(postId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectPostId) return;

    try {
      await postService.rejectPost(rejectPostId, rejectReason || undefined);
      setSuccess(t('community.success.postRejected'));
      // Update local state instead of refetching
      setPosts(prev => prev.map(p => p.id === rejectPostId ? { ...p, status: 'rejected' as const } : p));
      setShowRejectModal(false);
      setRejectPostId(null);
      setRejectReason('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.generic'));
    }
  };

  const handleTogglePin = async (postId: number) => {
    try {
      await postService.togglePinPost(postId);
      // Update local state instead of refetching
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, pinned: !p.pinned } : p));
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.generic'));
    }
  };

  const handleDelete = async (postId: number) => {
    if (!window.confirm(t('community.confirm.deletePost'))) return;

    try {
      await postService.deletePost(postId);
      setSuccess(t('community.success.postDeleted'));
      // Update local state instead of refetching
      setPosts(prev => prev.filter(p => p.id !== postId));
      if (selectedPost?.id === postId) {
        setShowDetailModal(false);
        setSelectedPost(null);
      }
      setOpenPostMenuId(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('community.errors.generic'));
    }
  };


  const handleReportPost = (postId: number) => {
    setReportPostId(postId);
    setReportReason('');
    setShowReportModal(true);
    setOpenPostMenuId(null);
  };

  const handleConfirmReport = async () => {
    if (!reportPostId || !reportReason.trim()) {
      setError(t('community.errors.reportReason'));
      return;
    }

    try {
      await postService.reportPost(reportPostId, reportReason);
      setSuccess(t('community.success.reportSent'));
      setShowReportModal(false);
      setReportPostId(null);
      setReportReason('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err: any) {
      setError(t('community.errors.report'));
    }
  };

  const handleToggleNotification = (postId: number) => {
    const newState = !postNotifications[postId];
    setPostNotifications(prev => ({
      ...prev,
      [postId]: newState
    }));

    // Lưu vào localStorage
    try {
      const stored = localStorage.getItem('postNotifications');
      const notifications = stored ? JSON.parse(stored) : {};
      notifications[postId] = newState;
      localStorage.setItem('postNotifications', JSON.stringify(notifications));
    } catch (err) {
      console.error('Error saving notification preference:', err);
    }



    setSuccess(newState ? t('community.success.notificationOn') : t('community.success.notificationOff'));
    setOpenPostMenuId(null);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEditPost = (post: Post) => {
    setEditingPostId(post.id);
    setEditFormData({
      title: post.title,
      content: post.content,
      tags: post.tags || [],
      category_id: post.category_id ?? null,
    });
    setOpenPostMenuId(null);
  };

  const handleSaveEdit = async (postId: number) => {
    if (!editFormData.title.trim()) {
      setError(t('community.errors.missingTitle'));
      return;
    }
    if (!editFormData.content.trim()) {
      setError(t('community.errors.missingContent'));
      return;
    }

    try {
      // Get follower IDs from localStorage - users who are following this post
      // Note: In a real app, this would come from backend. For now, we use localStorage
      // which only tracks current user's preferences. In production, you'd need a post_followers table.
      const stored = localStorage.getItem('postNotifications');
      const notifications = stored ? JSON.parse(stored) : {};

      // If current user is following this post, include their ID
      const followerIds: number[] = [];
      if (notifications[postId] && user?.id) {
        followerIds.push(user.id);
      }

      // Include follower IDs in update request (if any)
      const updateData = {
        ...editFormData,
        ...(followerIds.length > 0 ? { follower_ids: followerIds } : {})
      };

      const result = await postService.updatePost(postId, updateData);
      if (result.post) {
        setPosts(prev => prev.map(p => (p.id === postId ? result.post : p)));
        if (selectedPost?.id === postId) {
          setSelectedPost(result.post);
        }
      } else {
        // Fallback if post is missing in response
        const categorySummary = getCategorySummary(editFormData.category_id);
        setPosts(prev => prev.map(p =>
          p.id === postId
            ? {
              ...p,
              title: editFormData.title,
              content: editFormData.content,
              tags: editFormData.tags,
              category_id: editFormData.category_id ?? null,
              category: categorySummary,
              updated_at: new Date().toISOString()
            }
            : p
        ));
        if (selectedPost?.id === postId) {
          setSelectedPost({
            ...selectedPost,
            title: editFormData.title,
            content: editFormData.content,
            tags: editFormData.tags,
            category_id: editFormData.category_id ?? null,
            category: categorySummary,
            updated_at: new Date().toISOString(),
          });
        }
      }
      setEditingPostId(null);
      setEditFormData({ title: '', content: '', tags: [], category_id: null });
      setSuccess(result.message || t('community.success.postUpdated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || t('community.errors.updatePost');
      setError(errorMessage);
      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('.error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Real-time current time for accurate time display
  const [currentTime, setCurrentTime] = useState(() => {
    const now = new Date();
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
    const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
    return vietnamTime;
  });

  // Update current time every 1 minute for real-time display
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
      const vietnamTime = utcTime + (7 * 60 * 60 * 1000);
      setCurrentTime(vietnamTime);
    };

    // Update immediately
    updateTime();

    // Then update every 1 minute (60000ms) for accurate time display
    const timeInterval = setInterval(updateTime, 60000);

    return () => clearInterval(timeInterval);
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    // Convert to Vietnam timezone (UTC+7)
    const utcTime = date.getTime();
    const vietnamTime = utcTime + (7 * 60 * 60 * 1000);

    // Calculate difference using current time
    const diffMs = currentTime - vietnamTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('community.labels.justNow');
    if (diffMins < 60) return t('community.labels.minsAgo', { count: diffMins });
    if (diffHours < 24) return t('community.labels.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('community.labels.daysAgo', { count: diffDays });
    return date.toLocaleDateString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', { timeZone: 'Asia/Ho_Chi_Minh' });
  };

  const getAuthorInitials = (author: Post['author']) => {
    if (author.full_name) {
      return author.full_name
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }
    return author.username[0].toUpperCase();
  };

  const getAuthorAvatar = (author: Post['author']) => {
    if (author.avatar) {
      return getMediaUrl(author.avatar);
    }
    return null;
  };







  // Helper functions
  const getFileUrl = (path: string) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return getMediaUrl(path);
  };

  const isImageUrlByPath = (path: string) => {
    if (!path) return false;
    const ext = path.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext || '');
  };

  const isYouTubeUrl = (url: string) =>
    url.includes('youtube.com') || url.includes('youtu.be');

  const getYouTubeEmbedUrl = (url: string) => {
    if (url.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${url.split('/').pop()}`;
    }
    if (url.includes('watch?v=')) {
      return `https://www.youtube.com/embed/${url.split('v=')[1]?.split('&')[0]}`;
    }
    return url;
  };

  const renderAttachmentPreview = (path: string) => {
    const fileUrl = getFileUrl(path);
    if (isImageUrlByPath(fileUrl)) {
      return (
        <div className="mb-2">
          <button
            type="button"
            onClick={() => setImagePreviewUrl(fileUrl)}
            className="inline-block max-w-[180px] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-xl"
          >
            <div className="rounded-xl border border-gray-100 shadow-sm overflow-hidden bg-gray-50">
              <img
                src={fileUrl}
                alt="Ảnh đính kèm"
                className="w-full h-40 object-cover transition-transform duration-200 hover:scale-[1.02]"
              />
            </div>
          </button>
        </div>
      );
    }

    return (
      <div className="mb-2">
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
          Tệp đính kèm
        </a>
      </div>
    );
  };

  const renderInlineVideo = (rawUrl: string, className = 'w-full max-w-md rounded-lg') => {
    const fullUrl = getFileUrl(rawUrl);
    const isYouTube = isYouTubeUrl(fullUrl);
    return (
      <div className="relative group mb-2">
        {isYouTube ? (
          <iframe
            width="100%"
            height="260"
            src={getYouTubeEmbedUrl(fullUrl)}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className={`${className} bg-black`}
          />
        ) : (
          <video
            src={fullUrl}
            controls
            className={`${className} bg-black object-cover`}
          />
        )}
        <button
          type="button"
          onClick={() => setVideoPreviewUrl(fullUrl)}
          className="absolute top-2 left-2 w-8 h-8 rounded-full bg-white/80 text-gray-700 flex items-center justify-center shadow hover:bg-white"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-18h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3" />
          </svg>
        </button>
      </div>
    );
  };

  interface VideoUploadCardProps {
    previewUrl?: string | null;
    fileName?: string;
    isUploading?: boolean;
    onRemove?: () => void;
    compact?: boolean;
  }

  const VideoUploadCard: React.FC<VideoUploadCardProps> = ({ previewUrl, fileName, isUploading, onRemove, compact }) => (
    <div className={`relative ${compact ? 'w-40 h-24' : 'w-48 h-28'} border border-gray-200 rounded-xl overflow-hidden bg-black/5`}>
      {isUploading ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-white/80 text-gray-700 text-xs gap-1">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          <span className="font-medium">Đang xử lý...</span>
          <span className="text-[11px] text-gray-500">Có thể mất vài phút</span>
        </div>
      ) : previewUrl ? (
        <video src={previewUrl} controls className="w-full h-full object-cover bg-black" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 text-xs gap-1">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span>{fileName || 'Video đã chọn'}</span>
        </div>
      )}
      {previewUrl && (
        <button
          type="button"
          onClick={() => previewUrl && setVideoPreviewUrl(previewUrl)}
          className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-white/80 text-gray-700 flex items-center justify-center shadow hover:bg-white"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-18h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3" />
          </svg>
        </button>
      )}
      {onRemove && !isUploading && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/80 text-gray-600 hover:text-red-600 shadow flex items-center justify-center"
        >
          ×
        </button>
      )}
    </div>
  );

  interface VideoLinkCardProps {
    url: string;
    onRemove?: () => void;
    compact?: boolean;
  }

  const VideoLinkCard: React.FC<VideoLinkCardProps> = ({ url, onRemove, compact }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const fullUrl = getFileUrl(url);
    const isYoutube = isYouTubeUrl(fullUrl);

    return (
      <div className={`relative ${compact ? 'w-44 h-28' : 'w-56 h-32'} border border-gray-200 rounded-2xl overflow-hidden bg-black/5`}>
        {isYoutube ? (
          <iframe
            src={getYouTubeEmbedUrl(fullUrl)}
            title="Xem trước video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full bg-black"
          />
        ) : (
          <video src={fullUrl} controls className="w-full h-full object-cover bg-black" />
        )}
        <button
          type="button"
          onClick={() => setVideoPreviewUrl(fullUrl)}
          className="absolute top-2 left-2 w-8 h-8 rounded-full bg-white/80 text-gray-700 flex items-center justify-center shadow hover:bg-white"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m0 8v3a2 2 0 002 2h3m8-18h3a2 2 0 012 2v3m0 8v3a2 2 0 01-2 2h-3" />
          </svg>
        </button>
        <div className="absolute top-1.5 right-1.5">
          <button
            type="button"
            onClick={() => setMenuOpen(prev => !prev)}
            className="w-7 h-7 rounded-full bg-white/90 text-gray-600 flex items-center justify-center shadow hover:bg-white"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.75a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 text-left z-10">
              <button
                type="button"
                className="w-full px-3 py-2 text-gray-700 hover:bg-gray-50 text-sm text-left"
                disabled
              >
                Chỉnh thumbnail
                <span className="block text-[11px] text-gray-400">Sắp ra mắt</span>
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onRemove();
                  }}
                  className="w-full px-3 py-2 text-red-600 hover:bg-red-50 text-sm text-left font-medium"
                >
                  Xóa
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  type FlattenedReply = {
    comment: Comment;
    depth: number;
    parentAuthor?: string;
  };

  const flattenReplies = (comment: Comment, depth: number = 1): FlattenedReply[] => {
    const replies = comment.replies || [];
    const parentAuthorName = comment.author.full_name || comment.author.username;
    return replies.flatMap(reply => [
      { comment: reply, depth, parentAuthor: parentAuthorName },
      ...flattenReplies(reply, depth + 1)
    ]);
  };

  const updateCommentLikesState = (items: Comment[], commentId: number, updatedFields: Partial<Comment>): Comment[] => {
    return items.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, ...updatedFields };
      }
      if (comment.replies && comment.replies.length > 0) {
        return { ...comment, replies: updateCommentLikesState(comment.replies, commentId, updatedFields) };
      }
      return comment;
    });
  };

  const removeCommentById = (items: Comment[], commentId: number): Comment[] => {
    return items
      .filter(comment => comment.id !== commentId)
      .map(comment => {
        if (comment.replies && comment.replies.length > 0) {
          return { ...comment, replies: removeCommentById(comment.replies, commentId) };
        }
        return comment;
      });
  };

  const resetReplyComposer = () => {
    setReplyContent('');
    setReplyingTo(null);
    setReplyAttachedFile(null);
    setReplyLinkUrl('');
    setReplyVideoUrl('');
    setReplyVideoFile(null);
    setReplyShowLinkModal(false);
    setReplyShowVideoModal(false);
    setReplyShowEmojiPicker(false);
    setReplyShowGifPicker(false);
    setShowReplyActions(false);
  };

  const renderReplyComposer = (parentId: number, targetAuthorName: string) => {
    const replyHasExtras = Boolean(replyAttachedFile || replyLinkUrl || replyVideoUrl || replyVideoFile);

    return (
      <form onSubmit={(e) => handleSubmitReply(e, parentId)} className="mt-3">
        <div className="flex gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <textarea
              ref={(el) => {
                if (replyingTo === parentId) {
                  replyTextareaRef.current = el;
                } else if (replyTextareaRef.current === el) {
                  replyTextareaRef.current = null;
                }
              }}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder={`Trả lời ${targetAuthorName}...`}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm"
            />

            <div className="flex items-center justify-between mt-2 flex-wrap gap-3">
              <input
                id={replyFileInputId}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setReplyAttachedFile(file);
                  }
                }}
              />
              <div className="relative" ref={replyActionsRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowReplyActions(prev => !prev);
                    setReplyShowEmojiPicker(false);
                    setReplyShowGifPicker(false);
                  }}
                  className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center gap-1"
                  title="Tùy chọn trả lời"
                >
                  ⋮
                </button>

                {showReplyActions && (
                  <div className="absolute z-20 mt-2 right-0 w-56 bg-white border border-gray-200 rounded-2xl shadow-lg p-2 flex flex-col gap-1">
                    <label
                      htmlFor={replyFileInputId}
                      className="px-3 py-2 rounded-xl text-sm text-gray-700 hover:bg-gray-100 transition cursor-pointer flex items-center gap-2"
                      onClick={() => setShowReplyActions(false)}
                    >
                      📎
                      <span>Đính kèm file</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowLinkModal(true);
                        setReplyShowVideoModal(false);
                        setReplyShowEmojiPicker(false);
                        setReplyShowGifPicker(false);
                        setShowReplyActions(false);
                      }}
                      className="px-3 py-2 rounded-xl text-sm text-left text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                    >
                      🔗
                      <span>Thêm link</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowVideoModal(true);
                        setReplyShowLinkModal(false);
                        setReplyShowEmojiPicker(false);
                        setReplyShowGifPicker(false);
                        setShowReplyActions(false);
                      }}
                      className="px-3 py-2 rounded-xl text-sm text-left text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                    >
                      🎬
                      <span>Thêm video</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowEmojiPicker(prev => {
                          const next = !prev;
                          if (next) {
                            setReplyShowGifPicker(false);
                          }
                          return next;
                        });
                        setShowReplyActions(false);
                      }}
                      className="px-3 py-2 rounded-xl text-sm text-left text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                    >
                      😊
                      <span>Chèn emoji</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setReplyShowGifPicker(prev => {
                          const next = !prev;
                          if (next) {
                            setReplyShowEmojiPicker(false);
                          }
                          return next;
                        });
                        setShowReplyActions(false);
                      }}
                      className="px-3 py-2 rounded-xl text-sm text-left text-gray-700 hover:bg-gray-100 transition flex items-center gap-2"
                    >
                      GIF
                      <span>Thêm GIF</span>
                    </button>
                  </div>
                )}

                {replyShowEmojiPicker && (
                  <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-72 max-h-64 overflow-y-auto z-20">
                    <div className="grid grid-cols-8 gap-2">
                      {emojiOptions.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => {
                            setReplyContent(prev => prev + emoji);
                            setReplyShowEmojiPicker(false);
                          }}
                          className="text-2xl hover:bg-gray-100 rounded p-1 transition"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {replyShowGifPicker && (
                  <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-96 max-h-80 overflow-y-auto z-20">
                    <div className="mb-3">
                      <input
                        type="text"
                        placeholder="Tìm kiếm GIF..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onChange={(e) => {
                          // Handle GIF search
                        }}
                      />
                    </div>
                    <div className="text-sm text-gray-500 text-center py-6">
                      Tính năng GIF đang được phát triển...
                      <br />
                      <span className="text-xs">Sẽ tích hợp GIPHY API</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(replyAttachedFile || replyLinkUrl || replyVideoUrl || replyVideoFile) && (
              <div className="basis-full flex flex-wrap gap-3">
                {replyAttachedFile && (
                  <div className="relative w-28 h-28 border border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50">
                    {isImageFile(replyAttachedFile) && replyAttachedPreviewUrl ? (
                      <img src={replyAttachedPreviewUrl} alt={replyAttachedFile.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-gray-500 text-xs px-2 text-center">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="mt-1 truncate w-full">{replyAttachedFile.name}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setReplyAttachedFile(null)}
                      className="absolute top-1 right-1 bg-white/80 text-gray-600 hover:text-red-600 rounded-full w-6 h-6 flex items-center justify-center shadow"
                    >
                      ×
                    </button>
                  </div>
                )}
                {replyVideoFile && (
                  <VideoUploadCard
                    previewUrl={replyVideoPreviewUrl}
                    fileName={replyVideoFile.name}
                    isUploading={submittingComment && submittingReplyTo === parentId}
                    onRemove={() => setReplyVideoFile(null)}
                    compact
                  />
                )}
                {replyLinkUrl && (
                  <div className="relative flex items-center gap-2 px-3 py-2 border border-blue-200 rounded-lg bg-blue-50 text-blue-700 text-xs max-w-[220px]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span className="truncate flex-1">{replyLinkUrl}</span>
                    <button
                      type="button"
                      onClick={() => setReplyLinkUrl('')}
                      className="text-gray-500 hover:text-red-600"
                    >
                      ×
                    </button>
                  </div>
                )}
                {replyVideoUrl && (
                  <VideoLinkCard
                    url={replyVideoUrl}
                    onRemove={() => setReplyVideoUrl('')}
                    compact
                  />
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="submit"
              disabled={submittingComment || (!replyContent.trim() && !replyHasExtras)}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingComment ? 'Đang gửi...' : 'Gửi'}
            </button>
            <button
              type="button"
              onClick={resetReplyComposer}
              className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition"
            >
              Hủy
            </button>
          </div>
        </div>
      </form>
    );
  };

  const renderReplyThread = (replyItem: Comment): JSX.Element => {
    const replyAvatarUrl = getMediaUrl(replyItem.author.avatar);
    const replyInitials = replyItem.author.full_name
      ? replyItem.author.full_name
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
      : replyItem.author.username[0].toUpperCase();

    return (
      <div key={replyItem.id} className="flex gap-2">
        <Link
          to={`/u/${encodeURIComponent(replyItem.author.username)}`}
          className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center font-semibold overflow-hidden flex-shrink-0 text-xs cursor-pointer transition-transform hover:scale-105"
        >
          {replyAvatarUrl ? (
            <img
              src={replyAvatarUrl}
              alt={replyItem.author.full_name || replyItem.author.username}
              className="w-full h-full object-cover"
            />
          ) : (
            <span>{replyInitials}</span>
          )}
        </Link>
        <div className="flex-1">
          <div className="mb-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Link
                to={`/u/${encodeURIComponent(replyItem.author.username)}`}
                className={`font-semibold text-xs hover:underline ${replyItem.author.role === 'admin' ? 'text-red-600' : 'text-gray-900'}`}
              >
                {replyItem.author.full_name || replyItem.author.username}
              </Link>
              {selectedPost && replyItem.author.id === selectedPost.author.id && (
                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                  Tác giả
                </span>
              )}
              {replyItem.author.role === 'admin' && (
                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                  Quản trị viên
                </span>
              )}
              <span className="text-xs text-gray-500">
                {formatDate(replyItem.created_at)}
              </span>
            </div>
            <div className="text-gray-900 text-xs whitespace-pre-wrap mb-2">
              {formatContent(replyItem.content)}
            </div>
            {replyItem.attachment_url && renderAttachmentPreview(replyItem.attachment_url)}
            {replyItem.link_url && (
              <div className="mb-2">
                <a
                  href={replyItem.link_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  {replyItem.link_url}
                </a>
              </div>
            )}
            {replyItem.video_url && (
              <div className="mb-2">
                {replyItem.video_url.includes('youtube.com') || replyItem.video_url.includes('youtu.be') ? (
                  <iframe
                    width="100%"
                    height="315"
                    src={replyItem.video_url.includes('youtu.be')
                      ? `https://www.youtube.com/embed/${replyItem.video_url.split('/').pop()}`
                      : `https://www.youtube.com/embed/${replyItem.video_url.split('v=')[1]?.split('&')[0]}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="rounded-lg"
                  />
                ) : replyItem.video_url.startsWith('http') ? (
                  <video
                    src={replyItem.video_url}
                    controls
                    className="w-full max-w-md rounded-lg"
                  />
                ) : (
                  <video
                    src={getMediaUrl(replyItem.video_url)}
                    controls
                    className="w-full max-w-md rounded-lg"
                  />
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
              </svg>
              <span>0</span>
            </button>
            <button
              onClick={() => {
                if (replyingTo === replyItem.id) {
                  setReplyingTo(null);
                  setReplyContent('');
                } else {
                  setReplyingTo(replyItem.id);
                  setReplyContent('');
                }
              }}
              className="text-xs text-gray-600 hover:text-gray-900 font-medium"
            >
              Trả lời
            </button>
            {user && user.id === replyItem.author.id && (
              <button
                onClick={() => handleDeleteComment(replyItem.id)}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Xóa
              </button>
            )}
          </div>

          {replyingTo === replyItem.id && (
            <form onSubmit={(e) => handleSubmitReply(e, replyItem.id)} className="mt-2">
              <div className="flex gap-2">
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder={`Trả lời ${replyItem.author.full_name || replyItem.author.username}...`}
                  rows={2}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-xs"
                  required
                />
                <div className="flex flex-col gap-2">
                  <button
                    type="submit"
                    disabled={submittingComment || !replyContent.trim()}
                    className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingComment ? 'Đang gửi...' : 'Gửi'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyContent('');
                    }}
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-lg hover:bg-gray-200 transition"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            </form>
          )}

          {replyItem.replies && replyItem.replies.length > 0 && (
            <div className="mt-3 ml-4 space-y-3 border-l-2 border-blue-500 pl-4">
              {replyItem.replies.map((nestedReply) => renderReplyThread(nestedReply))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const filteredPosts = posts;


  const handleBannerClose = () => {
    sessionStorage.setItem('eventBannerClosed', 'true');
    setShowNewYearBanner(false);
  };

  // Render immediately - don't wait for anything
  return (
    <>
      {showNewYearBanner && (
        <NewYearEventBanner onClose={handleBannerClose} />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}
        {searchQuery && (
          <div className="mb-4 flex flex-wrap items-center gap-3 p-4 bg-blue-50 border border-blue-200 text-blue-900 rounded-xl">
            <span className="text-sm">
              Kết quả cho "<span className="font-semibold">{searchQuery}</span>"
            </span>
            <button
              type="button"
              onClick={clearSearchQuery}
              className="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
            >
              Xóa tìm kiếm
            </button>
          </div>
        )}

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* Main feed */}
          <div className="space-y-6">
            {/* Compose */}
            <div className="bg-surface border border-border rounded-2xl shadow-sm p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-semibold overflow-hidden">
                  {user?.avatar ? (
                    <img
                      src={getAuthorAvatar({ avatar: user.avatar, username: user.username, full_name: user.full_name, id: user.id, role: user.role }) || ''}
                      alt={user.full_name || user.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span>{user?.full_name?.[0]?.toUpperCase() || user?.username[0]?.toUpperCase() || 'U'}</span>
                  )}
                </div>
                <div className="flex-1">
                  {!showCreateForm ? (
                    <>
                      <div
                        onClick={() => setShowCreateForm(true)}
                        className="border border-border rounded-xl px-4 py-3 text-muted-foreground hover:border-primary transition cursor-pointer"
                      >
                        {t('community.labels.composePlaceholder')}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-4 text-sm text-foreground">
                        {quickActions.map((action, idx) => {
                          const colors = ['bg-primary-soft hover:bg-primary-soft-border text-primary-soft-foreground', 'bg-success-soft hover:bg-success-soft-border text-success-soft-foreground', 'bg-accent-soft hover:bg-border text-accent-soft-foreground', 'bg-info-soft hover:bg-info-soft-border text-info-soft-foreground'];
                          return (
                            <button
                              key={action.slug ?? action.label}
                              type="button"
                              onClick={action.slug ? () => handleCategoryFilterChange(action.slug || null) : undefined}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition ${action.slug ? colors[idx % colors.length] : 'bg-muted cursor-default'}`}
                            >
                              <span>{action.icon}</span>
                              <span>{action.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder={t('community.labels.titlePlaceholder')}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                      <textarea
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        placeholder={t('community.labels.contentPlaceholder')}
                        rows={6}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                        required
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <label className="block text-sm font-medium text-gray-700">{t('community.labels.category')}</label>
                          <select
                            value={formData.category_id ?? ''}
                            onChange={handleCategorySelect}
                            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900"
                          >
                            <option value="">{t('community.labels.selectCategory')}</option>
                            {postCategories.map((category) => (
                              <option key={category.id} value={category.id}>
                                {category.icon ? `${category.icon} ` : ''}{category.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">{t('community.labels.tags')}</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={tagInput}
                              onChange={(e) => setTagInput(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddTag();
                                }
                              }}
                              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                              placeholder={t('community.labels.tagPlaceholder')}
                            />
                            <button
                              type="button"
                              onClick={handleAddTag}
                              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                            >
                              {t('community.labels.add')}
                            </button>
                          </div>
                        </div>
                      </div>
                      {formData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formData.tags.map((tag) => (
                            <span key={tag} className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm flex items-center gap-2">
                              {tag}
                              <button type="button" onClick={() => handleRemoveTag(tag)} className="text-primary-600 hover:text-primary-900">
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Video Upload */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">{t('community.labels.videoOptional')}</label>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleVideoChange}
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        />
                        {videoPreview && (
                          <div className="relative">
                            <video src={videoPreview} controls className="w-full max-h-64 rounded-lg" />
                            <button
                              type="button"
                              onClick={() => {
                                setVideoFile(null);
                                setVideoPreview(null);
                              }}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Thumbnail Upload */}
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">{t('community.labels.thumbnailOptional')}</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailChange}
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        />
                        {thumbnailPreview && (
                          <div className="relative">
                            <img src={thumbnailPreview} alt="Thumbnail" className="w-full max-h-48 object-cover rounded-lg" />
                            <button
                              type="button"
                              onClick={() => {
                                setThumbnailFile(null);
                                setThumbnailPreview(null);
                              }}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={submitting}
                          className="px-6 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-medium hover:shadow-medium transition disabled:opacity-50"
                        >
                          {submitting ? t('community.labels.posting') : t('community.labels.post')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateForm(false);
                            setFormData({ title: '', content: '', tags: [], category_id: null });
                            setVideoFile(null);
                            setVideoPreview(null);
                            setThumbnailFile(null);
                            setThumbnailPreview(null);
                            setError('');
                          }}
                          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition"
                        >
                          {t('community.labels.cancel')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-soft p-6 space-y-4">
              <div className="flex flex-wrap gap-2">
                {categoryFilters.map((filter) => (
                  <button
                    key={filter.slug ?? 'all'}
                    onClick={() => handleCategoryFilterChange(filter.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition flex items-center gap-2 ${activeCategorySlug === filter.slug
                      ? 'bg-primary-600 text-white shadow-soft'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {filter.icon && <span>{filter.icon}</span>}
                    <span>{filter.label}</span>
                  </button>
                ))}
                {loadingCategories && (
                  <span className="text-sm text-gray-500 px-2 py-1">{t('community.labels.loading')}</span>
                )}
              </div>
            </div>

            {/* Posts - Show real UI immediately, data loads in background */}
            <div className="space-y-6">
              {filteredPosts.length > 0 && filteredPosts.map((post) => {
                const avatarUrl = getAuthorAvatar(post.author);
                const isAdminPost = post.author.role === 'admin';
                return (
                  <div
                    key={post.id}
                    className={`rounded-2xl shadow-soft p-6 transition hover:shadow-medium border post-fade-in ${post.pinned ? 'border-l-4 border-l-orange-400' : 'border-gray-200'
                      } ${isAdminPost
                        ? 'bg-gradient-to-br from-rose-50 via-white to-rose-50 border-rose-200'
                        : 'bg-white'
                      }`}
                  >
                    <div className="flex items-start gap-4">
                      <Link
                        to={`/u/${encodeURIComponent(post.author.username)}`}
                        className={`w-12 h-12 rounded-full text-white flex items-center justify-center font-semibold overflow-hidden cursor-pointer transition-transform hover:scale-105 ${isAdminPost ? 'bg-gradient-to-br from-red-500 to-orange-500 shadow-lg shadow-red-200/70' : 'bg-gradient-to-br from-red-500 to-orange-400'
                          }`}
                      >
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={post.author.full_name || post.author.username} className="w-full h-full object-cover" />
                        ) : (
                          <span>{getAuthorInitials(post.author)}</span>
                        )}
                      </Link>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <div>
                            <Link
                              to={`/u/${encodeURIComponent(post.author.username)}`}
                              className={`flex items-center gap-2 font-semibold hover:underline ${isAdminPost ? 'text-red-600' : 'text-gray-900'}`}
                            >
                              {post.author.full_name || post.author.username}
                              {isAdminPost && (
                                <span className="text-xs uppercase tracking-wide bg-red-100 text-red-600 px-2 py-1 rounded-full">
                                  {t('community.labels.admin')}
                                </span>
                              )}
                              {post.pinned && (
                                <span className="text-xs uppercase tracking-wide bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                                  {t('community.labels.pinned')}
                                </span>
                              )}
                              {post.status === 'pending' && user?.role === 'admin' && (
                                <span className="text-xs uppercase tracking-wide bg-yellow-100 text-yellow-600 px-2 py-1 rounded-full">
                                  {t('community.labels.pending')}
                                </span>
                              )}
                            </Link>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <span className="font-medium text-blue-500">●</span>
                              <span>{formatDate(post.created_at)}</span>
                              {post.category && (
                                <>
                                  <span>•</span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCategoryFilterChange(post.category?.slug || null);
                                    }}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium hover:bg-gray-200"
                                    title={t('community.labels.filterByCategory')}
                                  >
                                    {post.category.icon && <span>{post.category.icon}</span>}
                                    <span>{post.category.name}</span>
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          {user?.role === 'admin' && post.status === 'pending' && (
                            <div className="ml-auto flex gap-2">
                              <button
                                onClick={() => handleApprove(post.id)}
                                className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition"
                              >
                                {t('community.labels.approve')}
                              </button>
                              <button
                                onClick={() => handleReject(post.id)}
                                className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition"
                              >
                                {t('community.labels.reject')}
                              </button>
                            </div>
                          )}
                          {/* Action buttons - Menu 3 chấm */}
                          {user && (
                            <div className="ml-auto post-menu-container relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenPostMenuId(openPostMenuId === post.id ? null : post.id);
                                }}
                                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                                title={t('community.labels.moreOptions')}
                              >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                              </button>

                              {openPostMenuId === post.id && (
                                <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                                  {user.id === post.author.id ? (
                                    // Menu cho chủ bài viết
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditPost(post);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        {t('community.labels.editPost')}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(post.id);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        {t('community.labels.deletePost')}
                                      </button>
                                    </>
                                  ) : (
                                    // Menu cho người dùng khác
                                    <>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReportPost(post.id);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                        {t('community.labels.report')}
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleNotification(post.id);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                      >
                                        {postNotifications[post.id] ? (
                                          <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                            {t('community.labels.turnOffNotify')}
                                          </>
                                        ) : (
                                          <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                            {t('community.labels.turnOnNotify')}
                                          </>
                                        )}
                                      </button>
                                    </>
                                  )}
                                  {/* Admin actions - Ghim/Bỏ ghim */}
                                  {user?.role === 'admin' && post.status === 'approved' && (
                                    <div className="border-t border-gray-200 my-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTogglePin(post.id);
                                          setOpenPostMenuId(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                        </svg>
                                        {post.pinned ? t('community.labels.unpin') : t('community.labels.pin')}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {post.tags && post.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {post.tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {editingPostId === post.id ? (
                          // Form chỉnh sửa bài viết
                          <div className="mt-4 space-y-4">
                            <input
                              type="text"
                              value={editFormData.title}
                              onChange={(e) => {
                                setEditFormData({ ...editFormData, title: e.target.value });
                                setError('');
                              }}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder={t('community.labels.postTitle')}
                              maxLength={255}
                            />
                            <textarea
                              value={editFormData.content}
                              onChange={(e) => {
                                setEditFormData({ ...editFormData, content: e.target.value });
                                setError('');
                              }}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[150px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder={t('community.labels.postContent')}
                            />
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.labels.category')}</label>
                              <select
                                value={editFormData.category_id ?? ''}
                                onChange={handleEditCategorySelect}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                              >
                                <option value="">{t('community.labels.noCategory')}</option>
                                {postCategories.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.icon ? `${category.icon} ` : ''}{category.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveEdit(post.id)}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                              >
                                {t('community.labels.save')}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingPostId(null);
                                  setEditFormData({ title: '', content: '', tags: [], category_id: null });
                                  setError('');
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                              >
                                {t('community.labels.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h3
                              className="mt-4 text-xl font-semibold text-gray-900 cursor-pointer hover:text-primary-600 transition"
                              onClick={() => {
                                setOpenPostMenuId(null);
                                handleViewDetail(post.id);
                              }}
                            >
                              {post.title}
                            </h3>
                            <div className="mt-3 text-gray-700 leading-relaxed line-clamp-3">
                              {post.content.split('\n').slice(0, 3).join('\n')}
                              {post.content.split('\n').length > 3 && '...'}
                            </div>
                          </>
                        )}

                        {/* Video Thumbnail */}
                        {(post.video_url || post.thumbnail_url) && (
                          <div
                            className="mt-4 cursor-pointer"
                            onClick={() => {
                              setOpenPostMenuId(null);
                              handleViewDetail(post.id);
                            }}
                          >
                            {post.thumbnail_url ? (
                              <div className="relative rounded-lg overflow-hidden bg-gray-900">
                                <img
                                  src={getFileUrl(post.thumbnail_url)}
                                  alt={t('community.labels.videoThumbnailAlt')}
                                  className="w-full h-64 object-cover"
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                  <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ) : post.video_url ? (
                              <div className="relative rounded-lg overflow-hidden bg-gray-900">
                                <video
                                  src={getFileUrl(post.video_url)}
                                  className="w-full h-64 object-cover"
                                  controls={false}
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                  <div className="w-16 h-16 rounded-full bg-white bg-opacity-90 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                    </svg>
                                  </div>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        )}

                        <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                          <button
                            onClick={() => handleLike(post.id)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${post.is_liked
                              ? 'bg-red-50 text-red-600 hover:bg-red-100'
                              : 'hover:bg-gray-100 text-gray-600'
                              }`}
                          >
                            <svg
                              className={`w-5 h-5 transition-all duration-200 ${post.is_liked
                                ? 'fill-red-600 scale-110'
                                : 'fill-none stroke-current'
                                } ${likingPostId === post.id ? 'animate-pulse' : ''}`}
                              viewBox="0 0 24 24"
                              strokeWidth="2"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            <span className="font-medium">{post.likes_count}</span>
                          </button>
                          <button
                            onClick={() => handleViewDetail(post.id)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
                          >
                            <span className="text-lg">💬</span>
                            <span className="font-medium">{post.comments_count}</span>
                          </button>
                          <button
                            onClick={() => handleViewDetail(post.id)}
                            className="ml-auto text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            {t('community.labels.viewDetails')} →
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
              {/* Show "Load more" button only when there are posts and more to load */}
              {filteredPosts.length > 0 && hasMorePosts && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={handleLoadMorePosts}
                    disabled={loadingMorePosts}
                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full font-medium hover:bg-gray-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingMorePosts ? t('community.labels.loadingMore') : t('community.labels.loadMorePosts')}
                  </button>
                </div>
              )}
              {/* Only show empty state when we're absolutely sure there's no data */}
              {filteredPosts.length === 0 && posts.length === 0 && !loading && !hasMorePosts && (
                <div className="text-center py-12 text-gray-500">
                  {t('community.labels.noPostsYet')}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-soft p-6 space-y-6">
              <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-orange-500 to-gray-400 text-white p-5">
                <h3 className="text-lg font-semibold">TaoThaoAIPro Community</h3>
                <p className="text-sm text-white/90 mt-2 max-w-xs">
                  {t('community.labels.communityDescription')}
                </p>
              </div>

              <div>
                <div className="text-sm text-gray-500 mb-2">{t('community.labels.overview')}</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-gray-900">{posts.length}</div>
                    <div className="text-xs text-gray-500">{t('community.labels.posts')}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-gray-900">45</div>
                    <div className="text-xs text-gray-500">{t('community.labels.online')}</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-gray-900">2</div>
                    <div className="text-xs text-gray-500">{t('community.labels.administrators')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Detail Modal */}
        {showDetailModal && selectedPost && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-90 flex items-start justify-center z-50 overflow-y-auto">
            <div className="bg-white w-full max-w-4xl min-h-screen">
              {/* Header - Sticky */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between z-10">
                <h2 className="text-lg font-semibold text-gray-900">{t('community.labels.postDetails')}</h2>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedPost(null);
                    setComments([]);
                    setCommentContent('');
                    setReplyingTo(null);
                    setReplyContent('');
                    setOpenPostMenuId(null);
                    setEditingPostId(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-light w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition"
                >
                  ×
                </button>
              </div>

              {/* Main Content */}
              <div className="px-8 py-6">
                {/* Author Info */}
                <div className="flex items-center gap-3 mb-6">
                  <Link
                    to={`/u/${encodeURIComponent(selectedPost.author.username)}`}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-400 text-white flex items-center justify-center font-semibold overflow-hidden flex-shrink-0 cursor-pointer transition-transform hover:scale-105"
                  >
                    {getAuthorAvatar(selectedPost.author) ? (
                      <img src={getAuthorAvatar(selectedPost.author) || ''} alt={selectedPost.author.full_name || selectedPost.author.username} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-base">{getAuthorInitials(selectedPost.author)}</span>
                    )}
                  </Link>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/u/${encodeURIComponent(selectedPost.author.username)}`}
                        className={`font-semibold text-base hover:underline ${selectedPost.author.role === 'admin' ? 'text-red-600' : 'text-gray-900'
                          }`}
                      >
                        {selectedPost.author.full_name || selectedPost.author.username}
                      </Link>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {t('community.labels.author')}
                      </span>
                      {selectedPost.author.role === 'admin' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          {t('community.labels.administrator')}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-2">
                      <span>{formatDate(selectedPost.created_at)}</span>
                      {selectedPost.tags && selectedPost.tags.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{selectedPost.tags[0]}</span>
                        </>
                      )}
                      {selectedPost.category && (
                        <>
                          <span>•</span>
                          <button
                            type="button"
                            onClick={() => {
                              handleCategoryFilterChange(selectedPost.category?.slug || null);
                              setShowDetailModal(false);
                              setSelectedPost(null);
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200"
                          >
                            {selectedPost.category.icon && <span>{selectedPost.category.icon}</span>}
                            <span>{selectedPost.category.name}</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title and Content - Edit mode or View mode */}
                {editingPostId === selectedPost.id ? (
                  // Form chỉnh sửa bài viết trong modal
                  <div className="mb-6 space-y-4">
                    <input
                      type="text"
                      value={editFormData.title}
                      onChange={(e) => {
                        setEditFormData({ ...editFormData, title: e.target.value });
                        setError('');
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-2xl font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('community.labels.postTitle')}
                      maxLength={255}
                    />
                    <textarea
                      value={editFormData.content}
                      onChange={(e) => {
                        setEditFormData({ ...editFormData, content: e.target.value });
                        setError('');
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg min-h-[200px] text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('community.labels.postContent')}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.labels.category')}</label>
                      <select
                        value={editFormData.category_id ?? ''}
                        onChange={handleEditCategorySelect}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        <option value="">{t('community.labels.noCategory')}</option>
                        {postCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.icon ? `${category.icon} ` : ''}{category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSaveEdit(selectedPost.id)}
                        className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                      >
                        {t('community.labels.saveChanges')}
                      </button>
                      <button
                        onClick={() => {
                          setEditingPostId(null);
                          setEditFormData({ title: '', content: '', tags: [], category_id: null });
                          setError('');
                        }}
                        className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                      >
                        {t('community.labels.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Title */}
                    <h1 className="text-2xl font-bold text-gray-900 leading-tight mb-6">{selectedPost.title}</h1>

                    {/* Content - Formatted */}
                    <div
                      className="text-gray-800 leading-relaxed mb-6 text-base cursor-pointer select-none"
                      onDoubleClick={() => handleDoubleClickLike(selectedPost.id)}
                      title={t('community.labels.doubleClickToLike')}
                    >
                      {formatContent(selectedPost.content)}
                    </div>
                  </>
                )}

                {/* Video/Media */}
                {(selectedPost.video_url || selectedPost.thumbnail_url) && (
                  <div className="mb-6">
                    {selectedPost.video_url ? (
                      <div className="bg-gray-900 rounded-lg overflow-hidden">
                        <video
                          src={getFileUrl(selectedPost.video_url)}
                          controls
                          className="w-full"
                          style={{ maxHeight: '600px' }}
                        />
                      </div>
                    ) : selectedPost.thumbnail_url ? (
                      <div className="relative rounded-lg overflow-hidden bg-gray-900">
                        <img
                          src={getFileUrl(selectedPost.thumbnail_url)}
                          alt={t('community.labels.postMedia')}
                          className="w-full object-cover"
                          style={{ maxHeight: '600px' }}
                        />
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Engagement Bar */}
                <div className="flex items-center justify-between py-4 border-t border-b border-gray-200 my-6">
                  <div className="flex items-center gap-6">
                    <button
                      onClick={() => handleLike(selectedPost.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${selectedPost.is_liked
                        ? 'bg-red-50 text-red-600 hover:bg-red-100'
                        : 'hover:bg-gray-100 text-gray-600'
                        }`}
                    >
                      <svg
                        className={`w-5 h-5 transition-all duration-200 ${selectedPost.is_liked
                          ? 'fill-red-600 scale-110'
                          : 'fill-none stroke-current'
                          } ${likingPostId === selectedPost.id ? 'animate-pulse' : ''}`}
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span className="font-medium">{selectedPost.likes_count}</span>
                    </button>
                    <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition text-gray-700">
                      <span className="text-lg">💬</span>
                      <span className="font-medium">{t('community.labels.commentsCount', { count: selectedPost.comments_count })}</span>
                    </button>
                  </div>
                  {/* Menu 3 chấm cho modal detail */}
                  {user && (
                    <div className="post-menu-container relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenPostMenuId(openPostMenuId === selectedPost.id ? null : selectedPost.id);
                        }}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
                        title={t('community.labels.options')}
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>

                      {openPostMenuId === selectedPost.id && (
                        <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                          {user.id === selectedPost.author.id ? (
                            // Menu cho chủ bài viết
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPost(selectedPost);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                {t('community.labels.editPost')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(t('community.labels.confirmDeletePost'))) {
                                    handleDelete(selectedPost.id);
                                    setShowDetailModal(false);
                                    setSelectedPost(null);
                                  }
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {t('community.labels.deletePost')}
                              </button>
                            </>
                          ) : (
                            // Menu cho người dùng khác
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReportPost(selectedPost.id);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                {t('community.labels.reportPostToAdmin')}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleNotification(selectedPost.id);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                {postNotifications[selectedPost.id] ? (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    {t('community.labels.turnOffPostNotifications')}
                                  </>
                                ) : (
                                  <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    {t('community.labels.turnOnPostNotifications')}
                                  </>
                                )}
                              </button>
                            </>
                          )}
                          {/* Admin actions - Ghim/Bỏ ghim */}
                          {user?.role === 'admin' && selectedPost.status === 'approved' && (
                            <div className="border-t border-gray-200 my-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePin(selectedPost.id);
                                  setOpenPostMenuId(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                </svg>
                                {selectedPost.pinned ? t('community.labels.unpin') : t('community.labels.pin')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Comments Section */}
                <div id="comments-section" className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {t('community.labels.commentsCount', { count: selectedPost.comments_count })}
                  </h3>

                  {/* Comment Form */}
                  <form onSubmit={handleSubmitComment} className="mb-6">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center font-semibold overflow-hidden flex-shrink-0">
                        {user?.avatar ? (
                          <img
                            src={getFileUrl(user.avatar)}
                            alt={user.full_name || user.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span>{user?.full_name?.[0]?.toUpperCase() || user?.username[0]?.toUpperCase() || 'U'}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={commentContent}
                          onChange={(e) => setCommentContent(e.target.value)}
                          placeholder={t('community.labels.writeComment')}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        />
                        <div className="flex items-center justify-between mt-2 flex-wrap gap-3">
                          <input
                            id={commentFileInputId}
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setAttachedFile(file);
                              }
                            }}
                          />
                          <div className="flex items-center gap-2">
                            <label
                              htmlFor={commentFileInputId}
                              className="px-3 py-2 text-base bg-gray-100 hover:bg-gray-200 rounded-lg transition cursor-pointer flex items-center justify-center min-w-[40px] h-10"
                              title={t('community.labels.attachFile')}
                            >
                              📎
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowLinkModal(true)}
                              className="px-3 py-2 text-base bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center min-w-[40px] h-10"
                              title={t('community.labels.addLink')}
                            >
                              🔗
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowVideoModal(true)}
                              className="px-3 py-2 text-base bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center min-w-[40px] h-10"
                              title={t('community.labels.addVideo')}
                            >
                              🎬
                            </button>
                            <div className="relative" ref={commentActionsRef}>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowEmojiPicker(prev => {
                                    const next = !prev;
                                    if (next) {
                                      setShowGifPicker(false);
                                    }
                                    return next;
                                  });
                                }}
                                className="px-3 py-2 text-base bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center min-w-[40px] h-10"
                                title={t('community.labels.insertEmoji')}
                              >
                                😊
                              </button>
                              {showEmojiPicker && (
                                <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80 max-h-64 overflow-y-auto z-20">
                                  <div className="grid grid-cols-8 gap-2">
                                    {emojiOptions.map((emoji) => (
                                      <button
                                        key={emoji}
                                        type="button"
                                        onClick={() => {
                                          setCommentContent(prev => prev + emoji);
                                          setShowEmojiPicker(false);
                                        }}
                                        className="text-2xl hover:bg-gray-100 rounded p-1 transition"
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() => {
                                  setShowGifPicker(prev => {
                                    const next = !prev;
                                    if (next) {
                                      setShowEmojiPicker(false);
                                    }
                                    return next;
                                  });
                                }}
                                className="px-3 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-lg transition flex items-center justify-center min-w-[40px] h-10"
                                title={t('community.labels.addGif')}
                              >
                                GIF
                              </button>
                              {showGifPicker && (
                                <div className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-96 max-h-96 overflow-y-auto z-20">
                                  <div className="mb-3">
                                    <input
                                      type="text"
                                      placeholder={t('community.labels.searchGif')}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      onChange={(e) => {
                                        // Handle GIF search
                                      }}
                                    />
                                  </div>
                                  <div className="text-sm text-gray-500 text-center py-8">
                                    {t('community.labels.gifFeatureUnderDevelopment')}
                                    <br />
                                    <span className="text-xs">{t('community.labels.willIntegrateGiphyApi')}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {(attachedFile || commentVideoFile || linkUrl || videoUrl) && (
                            <div className="basis-full flex flex-wrap gap-3">
                              {attachedFile && (
                                <div className="relative w-28 h-28 border border-dashed border-gray-300 rounded-xl overflow-hidden bg-gray-50">
                                  {isImageFile(attachedFile) && attachedPreviewUrl ? (
                                    <img src={attachedPreviewUrl} alt={attachedFile.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-gray-500 text-xs px-2 text-center">
                                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                      </svg>
                                      <span className="mt-1 truncate w-full">{attachedFile.name}</span>
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => setAttachedFile(null)}
                                    className="absolute top-1 right-1 bg-white/80 text-gray-600 hover:text-red-600 rounded-full w-6 h-6 flex items-center justify-center shadow"
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                              {commentVideoFile && (
                                <VideoUploadCard
                                  previewUrl={commentVideoPreviewUrl}
                                  fileName={commentVideoFile.name}
                                  isUploading={submittingComment && submittingReplyTo === null}
                                  onRemove={() => setCommentVideoFile(null)}
                                />
                              )}
                              {linkUrl && (
                                <div className="relative flex items-center gap-2 px-3 py-2 border border-blue-200 rounded-lg bg-blue-50 text-blue-700 text-xs max-w-[220px]">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  <span className="truncate flex-1">{linkUrl}</span>
                                  <button
                                    type="button"
                                    onClick={() => setLinkUrl('')}
                                    className="text-gray-500 hover:text-red-600"
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                              {videoUrl && (
                                <VideoLinkCard
                                  url={videoUrl}
                                  onRemove={() => setVideoUrl('')}
                                />
                              )}
                            </div>
                          )}

                          <button
                            type="submit"
                            disabled={submittingComment || (!commentContent.trim() && !attachedFile && !linkUrl && !videoUrl && !commentVideoFile)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {submittingComment ? t('community.labels.sending') : t('community.labels.send')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>

                  {/* Comments List */}
                  {loadingComments ? (
                    <div className="text-center py-8 text-gray-500">{t('community.labels.loading')}</div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      {t('community.labels.noComments')}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {comments.map((comment) => {
                        const commentAvatarUrl = comment.author.avatar
                          ? getMediaUrl(comment.author.avatar)
                          : null;
                        const commentInitials = comment.author.full_name
                          ? comment.author.full_name
                            .split(' ')
                            .map(w => w[0])
                            .join('')
                            .slice(0, 2)
                            .toUpperCase()
                          : comment.author.username[0].toUpperCase();
                        const replyThread = flattenReplies(comment);
                        const isExpanded = expandedComments[comment.id];
                        const maxCollapsedReplies = 2;
                        const visibleReplyThread = isExpanded ? replyThread : replyThread.slice(0, maxCollapsedReplies);

                        return (
                          <div key={comment.id} id={`comment-${comment.id}`} className="flex gap-3 scroll-mt-4">
                            <Link
                              to={`/u/${encodeURIComponent(comment.author.username)}`}
                              className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center font-semibold overflow-hidden flex-shrink-0 cursor-pointer transition-transform hover:scale-105"
                            >
                              {commentAvatarUrl ? (
                                <img
                                  src={commentAvatarUrl}
                                  alt={comment.author.full_name || comment.author.username}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-sm">{commentInitials}</span>
                              )}
                            </Link>
                            <div className="flex-1">
                              <div className="mb-1 bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <Link
                                    to={`/u/${encodeURIComponent(comment.author.username)}`}
                                    className={`font-semibold text-sm hover:underline ${comment.author.role === 'admin' ? 'text-red-600' : 'text-gray-900'
                                      }`}
                                  >
                                    {comment.author.full_name || comment.author.username}
                                  </Link>
                                  {selectedPost && comment.author.id === selectedPost.author.id && (
                                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                      Tác giả
                                    </span>
                                  )}
                                  {comment.author.role === 'admin' && (
                                    <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                                      Quản trị viên
                                    </span>
                                  )}
                                  <span className="text-xs text-gray-500">
                                    {formatDate(comment.created_at)}
                                  </span>
                                </div>
                                <div className="text-gray-900 text-sm whitespace-pre-wrap mb-2">
                                  {formatContent(comment.content)}
                                </div>
                                {comment.attachment_url && renderAttachmentPreview(comment.attachment_url)}
                                {comment.link_url && (
                                  <div className="mb-2">
                                    <a
                                      href={comment.link_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                      </svg>
                                      {comment.link_url}
                                    </a>
                                  </div>
                                )}
                                {comment.video_url && renderInlineVideo(comment.video_url)}
                              </div>
                              <div className="flex items-center gap-4">
                                <button
                                  onClick={() => handleToggleCommentLike(comment.id)}
                                  disabled={likingCommentId === comment.id}
                                  className={`flex items-center gap-1 text-xs transition ${comment.is_liked ? 'text-rose-500' : 'text-gray-600 hover:text-gray-900'
                                    } ${likingCommentId === comment.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <svg
                                    className="w-4 h-4"
                                    viewBox="0 0 24 24"
                                    fill={comment.is_liked ? 'currentColor' : 'none'}
                                    stroke="currentColor"
                                    strokeWidth={1.5}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M21.8 7.112c0-2.632-2.086-4.762-4.66-4.762-1.704 0-3.188.988-3.9 2.43-.712-1.442-2.196-2.43-3.9-2.43-2.574 0-4.66 2.13-4.66 4.762 0 6.138 8.56 9.92 8.56 9.92s8.56-3.782 8.56-9.92z" />
                                  </svg>
                                  <span>{comment.likes_count ?? 0}</span>
                                </button>
                                <button
                                  onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                                  className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                                >
                                  Trả lời
                                </button>
                                {user && user.id === comment.author.id && (
                                  <button
                                    onClick={() => handleDeleteComment(comment.id)}
                                    className="text-xs text-red-600 hover:text-red-700"
                                  >
                                    Xóa
                                  </button>
                                )}
                              </div>

                              {replyingTo === comment.id && renderReplyComposer(comment.id, comment.author.full_name || comment.author.username)}

                              {replyThread.length > 0 && (
                                <div className="mt-3 ml-8 space-y-3">
                                  {visibleReplyThread.map(({ comment: reply, parentAuthor }) => {
                                    const replyAvatarUrl = reply.author.avatar
                                      ? getMediaUrl(reply.author.avatar)
                                      : null;
                                    const replyInitials = reply.author.full_name
                                      ? reply.author.full_name
                                        .split(' ')
                                        .map(w => w[0])
                                        .join('')
                                        .slice(0, 2)
                                        .toUpperCase()
                                      : reply.author.username[0].toUpperCase();

                                    return (
                                      <div key={reply.id} className="flex gap-3">
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 text-white flex items-center justify-center font-semibold overflow-hidden flex-shrink-0 text-xs">
                                          {replyAvatarUrl ? (
                                            <img
                                              src={replyAvatarUrl}
                                              alt={reply.author.full_name || reply.author.username}
                                              className="w-full h-full object-cover"
                                            />
                                          ) : (
                                            <span>{replyInitials}</span>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <div className="mb-1 bg-white rounded-2xl border border-blue-100 shadow-sm px-4 py-3">
                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                              <span className={`font-semibold text-xs ${reply.author.role === 'admin' ? 'text-red-600' : 'text-gray-900'}`}>
                                                {reply.author.full_name || reply.author.username}
                                              </span>
                                              {selectedPost && reply.author.id === selectedPost.author.id && (
                                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                                  Tác giả
                                                </span>
                                              )}
                                              {reply.author.role === 'admin' && (
                                                <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                                                  Quản trị viên
                                                </span>
                                              )}
                                              <span className="text-xs text-gray-500">
                                                {formatDate(reply.created_at)}
                                              </span>
                                            </div>
                                            {parentAuthor && (
                                              <div className="text-xs text-blue-600 font-medium mb-1">
                                                @{parentAuthor}
                                              </div>
                                            )}
                                            <div className="text-gray-900 text-xs whitespace-pre-wrap mb-2">
                                              {formatContent(reply.content)}
                                            </div>
                                            {reply.attachment_url && renderAttachmentPreview(reply.attachment_url)}
                                            {reply.link_url && (
                                              <div className="mb-2">
                                                <a
                                                  href={reply.link_url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-xs"
                                                >
                                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                  </svg>
                                                  {reply.link_url}
                                                </a>
                                              </div>
                                            )}
                                            {reply.video_url && renderInlineVideo(reply.video_url, 'w-full max-w-sm rounded-lg')}
                                          </div>
                                          <div className="flex items-center gap-4">
                                            <button
                                              onClick={() => handleToggleCommentLike(reply.id)}
                                              disabled={likingCommentId === reply.id}
                                              className={`flex items-center gap-1 text-xs transition ${reply.is_liked ? 'text-rose-500' : 'text-gray-600 hover:text-gray-900'
                                                } ${likingCommentId === reply.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                              <svg
                                                className="w-3.5 h-3.5"
                                                viewBox="0 0 24 24"
                                                fill={reply.is_liked ? 'currentColor' : 'none'}
                                                stroke="currentColor"
                                                strokeWidth={1.5}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              >
                                                <path d="M21.8 7.112c0-2.632-2.086-4.762-4.66-4.762-1.704 0-3.188.988-3.9 2.43-.712-1.442-2.196-2.43-3.9-2.43-2.574 0-4.66 2.13-4.66 4.762 0 6.138 8.56 9.92 8.56 9.92s8.56-3.782 8.56-9.92z" />
                                              </svg>
                                              <span>{reply.likes_count ?? 0}</span>
                                            </button>
                                            <button
                                              onClick={() => setReplyingTo(replyingTo === reply.id ? null : reply.id)}
                                              className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                                            >
                                              Trả lời
                                            </button>
                                            {user && user.id === reply.author.id && (
                                              <button
                                                onClick={() => handleDeleteComment(reply.id)}
                                                className="text-xs text-red-600 hover:text-red-700"
                                              >
                                                Xóa
                                              </button>
                                            )}
                                          </div>

                                          {replyingTo === reply.id && renderReplyComposer(reply.id, reply.author.full_name || reply.author.username)}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {replyThread.length > maxCollapsedReplies && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedComments(prev => ({
                                          ...prev,
                                          [comment.id]: !isExpanded
                                        }))
                                      }
                                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      {isExpanded
                                        ? 'Ẩn bớt phản hồi'
                                        : `Xem thêm ${replyThread.length - maxCollapsedReplies} phản hồi`}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Jump to latest comment button */}
                  {comments.length > 0 && (
                    <div className="mt-6 text-center">
                      <button
                        onClick={() => {
                          const commentsSection = document.getElementById('comments-section');
                          if (commentsSection) {
                            commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Đi đến bình luận mới nhất
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Link Modal */}
        {showLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Thêm link</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nhập URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowLinkModal(false);
                    setLinkUrl('');
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    if (linkUrl.trim()) {
                      setShowLinkModal(false);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                >
                  Thêm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Video Modal */}
        {showVideoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Thêm video</h3>

              {/* Link Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link YouTube, Loom, Vimeo, hoặc Wistia
                </label>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="YouTube, Loom, Vimeo, or Wistia link"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">hoặc</span>
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tải video lên</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600 mb-2">Kéo thả video vào đây</p>
                  <input
                    id={commentVideoFileInputId}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setCommentVideoFile(file);
                        setVideoUrl('');
                      }
                    }}
                  />
                  <label
                    htmlFor={commentVideoFileInputId}
                    className="inline-block text-sm text-blue-600 hover:text-blue-700 cursor-pointer"
                  >
                    hoặc chọn file
                  </label>
                </div>
                {commentVideoFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                    <span className="truncate">{commentVideoFile.name}</span>
                    <button
                      onClick={() => setCommentVideoFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowVideoModal(false);
                    setVideoUrl('');
                    setCommentVideoFile(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    if (videoUrl.trim() || commentVideoFile) {
                      setShowVideoModal(false);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                >
                  Thêm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reply Link Modal */}
        {replyShowLinkModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Thêm link cho phản hồi</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nhập URL</label>
                <input
                  type="url"
                  value={replyLinkUrl}
                  onChange={(e) => setReplyLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setReplyShowLinkModal(false);
                    setReplyLinkUrl('');
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    if (replyLinkUrl.trim()) {
                      setReplyShowLinkModal(false);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                >
                  Thêm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reply Video Modal */}
        {replyShowVideoModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Thêm video cho phản hồi</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link YouTube, Loom, Vimeo, hoặc Wistia
                </label>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  <input
                    type="url"
                    value={replyVideoUrl}
                    onChange={(e) => setReplyVideoUrl(e.target.value)}
                    placeholder="YouTube, Loom, Vimeo, or Wistia link"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">hoặc</span>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Tải video lên</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600 mb-2">Kéo thả video vào đây</p>
                  <input
                    id={replyVideoModalFileInputId}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setReplyVideoFile(file);
                        setReplyVideoUrl('');
                      }
                    }}
                  />
                  <label
                    htmlFor={replyVideoModalFileInputId}
                    className="inline-block text-sm text-blue-600 hover:text-blue-700 cursor-pointer"
                  >
                    hoặc chọn file
                  </label>
                </div>
                {replyVideoFile && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                    <span className="truncate">{replyVideoFile.name}</span>
                    <button
                      onClick={() => setReplyVideoFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setReplyShowVideoModal(false);
                    setReplyVideoUrl('');
                    setReplyVideoFile(null);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    if (replyVideoUrl.trim() || replyVideoFile) {
                      setReplyShowVideoModal(false);
                    }
                  }}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                >
                  Thêm
                </button>
              </div>
            </div>
          </div>
        )}

        {imagePreviewUrl && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setImagePreviewUrl(null)}
          >
            <div
              className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setImagePreviewUrl(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center text-2xl leading-none hover:bg-black/80 transition"
              >
                ×
              </button>
              <img
                src={imagePreviewUrl}
                alt="Preview"
                className="w-full h-full max-h-[80vh] object-contain bg-black"
              />
            </div>
          </div>
        )}

        {/* Report Modal */}
        {showReportModal && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowReportModal(false);
              setReportPostId(null);
              setReportReason('');
            }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Báo cáo bài viết</h3>
              <p className="text-gray-600 mb-4">Vui lòng nhập lý do báo cáo bài viết này:</p>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập lý do báo cáo..."
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportPostId(null);
                    setReportReason('');
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Hủy
                </button>
                <button
                  onClick={handleConfirmReport}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                >
                  Gửi báo cáo
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowRejectModal(false);
              setRejectPostId(null);
              setRejectReason('');
            }}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Từ chối bài đăng</h3>
              <p className="text-gray-600 mb-4">Nhập lý do từ chối (tùy chọn):</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nhập lý do từ chối..."
                autoFocus
              />
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectPostId(null);
                    setRejectReason('');
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Hủy
                </button>
                <button
                  onClick={handleConfirmReject}
                  className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                >
                  Từ chối
                </button>
              </div>
            </div>
          </div>
        )}

        {videoPreviewUrl && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setVideoPreviewUrl(null)}
          >
            <div
              className="relative bg-black rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setVideoPreviewUrl(null)}
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/80 text-gray-700 flex items-center justify-center text-2xl hover:bg-white z-10"
              >
                ×
              </button>
              {isYouTubeUrl(videoPreviewUrl) ? (
                <iframe
                  src={getYouTubeEmbedUrl(videoPreviewUrl)}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-[70vh]"
                />
              ) : (
                <video src={videoPreviewUrl} controls autoPlay className="w-full h-[70vh] object-contain bg-black" />
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default CommunityPage;

